import type { UnavailableIdJSON } from "_shared/_api/by-ids/by-ids-result";
import {
  POLICY_REPLACEMENT_SCOPES,
  PolicyHoldingNegotiationJSON,
  PolicyReplacementAgreementJSON,
  PolicyReplacementPreviewAgreementJSON,
  PolicyReplacementPreviewJSON,
  PolicyReplacementScope,
  UNRECOGNIZED,
} from "work/policy-replacements/_schemas/policy-replacement-schema";

/**
 * The most agreements one replacement may reach: `CreatePolicyReplacementCommand.MaximumAgreements`
 * in the API, which refuses a longer list and creates nothing.
 */
export const POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS = 200;

/** How the page names one group of the preview, and what it says the replacement does to it. */
export type PolicyReplacementScopeDescription = {
  title: string;
  explanation: string;
  /** Whether the client may put an agreement in this group into the replacement. */
  selectable: boolean;
};

/**
 * Each group of the preview, in the words of the design's "Which agreements are in it" table. Only
 * Eligible and Revision in progress can take the replacement, and both start selected; every other
 * group is listed so nothing the client can see is dropped without a reason.
 */
export const POLICY_REPLACEMENT_SCOPE_DESCRIPTIONS: Record<PolicyReplacementScope, PolicyReplacementScopeDescription> = {
  Eligible: {
    title: "Will be revised",
    explanation:
      "Hired and running. Each agreement gets a revision that replaces the policy, and its vendor accepts or rejects it.",
    selectable: true,
  },
  RevisionInProgress: {
    title: "Waiting for a revision already open",
    explanation:
      "A revision of the agreement is being negotiated. The replacement waits until that revision ends, then opens its own.",
    selectable: true,
  },
  Refused: {
    title: "You may not revise",
    explanation:
      "You can see these agreements, but you do not lead their client team or you may not edit their policies.",
    selectable: false,
  },
  AlreadyReplacedHere: {
    title: "Already replaced",
    explanation: "Somebody already replaced this policy on these agreements. The newer document stays.",
    selectable: false,
  },
  NotHeld: {
    title: "Does not hold the policy",
    explanation: "You added these agreements, and they do not hold the document you chose for them.",
    selectable: false,
  },
  Ended: {
    title: "Ended",
    explanation: "Archived, past their end, or replaced by a later agreement. An ended agreement is not revised.",
    selectable: false,
  },
  NotYetHired: {
    title: "Not yet hired",
    explanation: "Still in their first negotiation. Change the policy in that negotiation.",
    selectable: false,
  },
  [UNRECOGNIZED]: {
    title: "Not recognized",
    explanation: "This page does not recognize what the server said about these agreements. Reload to update it.",
    selectable: false,
  },
};

export type PolicyReplacementPreviewGroup = PolicyReplacementScopeDescription & {
  scope: PolicyReplacementScope;
  rows: PolicyReplacementPreviewAgreementJSON[];
};

/**
 * Which agreements replacing one client policy would reach, grouped as the design defines, and which
 * of them the client has chosen.
 *
 * Selection is kept by row, not by agreement: the API answers every agreement added by hand with a
 * row of its own, so two rows can name one agreement (`addedWorkAgreementId` tells them apart).
 */
export default class PolicyReplacementPreview {
  public static fromJSON(json: PolicyReplacementPreviewJSON | null | undefined): PolicyReplacementPreview | null {
    return json ? new PolicyReplacementPreview(json) : null;
  }

  /** One row's identity: the id the client added when there was one, and the document it replaces there. */
  public static rowKey(row: PolicyReplacementPreviewAgreementJSON): string {
    return `${row.addedWorkAgreementId ?? row.workAgreementId}:${row.replacedDocumentId}`;
  }

  public static isSelectable(row: PolicyReplacementPreviewAgreementJSON): boolean {
    return POLICY_REPLACEMENT_SCOPE_DESCRIPTIONS[row.scope].selectable;
  }

  public readonly replacedDocumentId: string;
  public readonly agreements: readonly PolicyReplacementPreviewAgreementJSON[];
  public readonly firstNegotiations: readonly PolicyHoldingNegotiationJSON[];
  public readonly unavailable: readonly UnavailableIdJSON[];

  public get groups(): PolicyReplacementPreviewGroup[] {
    const order: PolicyReplacementScope[] = [...POLICY_REPLACEMENT_SCOPES, UNRECOGNIZED];
    return order
      .map((scope) => ({
        scope,
        ...POLICY_REPLACEMENT_SCOPE_DESCRIPTIONS[scope],
        rows: this.agreements.filter((row) => row.scope === scope),
      }))
      .filter((group) => group.rows.length > 0);
  }

  /**
   * The rows a replacement starts with: every one it can reach, up to
   * {@link POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS} agreements in the order the preview lists them, so
   * the page never starts with a selection the API would refuse. The client clears what it does not
   * want, and chooses among the rest by hand.
   */
  public get defaultSelection(): Set<string> {
    const agreements = new Set<string>();
    const selection = new Set<string>();
    for (const row of this.agreements) {
      if (!PolicyReplacementPreview.isSelectable(row)) continue;
      if (!agreements.has(row.workAgreementId)) {
        if (agreements.size >= POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS) continue;
        agreements.add(row.workAgreementId);
      }
      selection.add(PolicyReplacementPreview.rowKey(row));
    }
    return selection;
  }

  /** How many agreements the replacement could reach, counting an agreement two rows name once. */
  public get reachableAgreementCount(): number {
    return new Set(this.agreements.filter(PolicyReplacementPreview.isSelectable).map((row) => row.workAgreementId)).size;
  }

  public constructor(json: PolicyReplacementPreviewJSON) {
    this.replacedDocumentId = json.replacedDocumentId;
    this.agreements = json.agreements;
    this.firstNegotiations = json.firstNegotiations;
    this.unavailable = json.unavailable;
  }

  /**
   * The agreements a create command names, from the rows the client kept.
   *
   * A row that cannot take the replacement is never sent, whatever the selection says. One
   * agreement is sent once: a replacement has one target per agreement, so when two added rows end
   * at the same agreement the first kept row decides which policy on it is replaced.
   */
  public agreementsToReplace(selection: ReadonlySet<string>): PolicyReplacementAgreementJSON[] {
    const seen = new Set<string>();
    const agreements: PolicyReplacementAgreementJSON[] = [];
    for (const row of this.agreements) {
      if (!PolicyReplacementPreview.isSelectable(row)) continue;
      if (!selection.has(PolicyReplacementPreview.rowKey(row))) continue;
      if (seen.has(row.workAgreementId)) continue;
      seen.add(row.workAgreementId);
      agreements.push({ workAgreementId: row.workAgreementId, replacedDocumentId: row.replacedDocumentId });
    }
    return agreements;
  }
}
